import { z } from "zod";

/**
 * Sign-in steps that outlive a page load: a 2FA challenge or an unverified sign-up. Kept in
 * sessionStorage (this tab only, cleared when it closes) — never tokens, only ids and the email.
 */
const pendingSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("two-factor"), challengeId: z.string().min(16), email: z.string(), next: z.string().optional() }),
  z.object({ kind: z.literal("verify-email"), email: z.string() }),
]);

export type PendingAuth = z.infer<typeof pendingSchema>;

const STORAGE_KEY = "sagot:pending-auth";

export function readPendingAuth(): PendingAuth | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = pendingSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function writePendingAuth(value: PendingAuth): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked: the flow still works within this page view.
  }
}

export function clearPendingAuth(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
}

/** Only same-app relative paths are accepted as `?next=` targets (no open redirects). */
export function safeNext(value: string | null | undefined): string | undefined {
  if (!value?.startsWith("/") || value.startsWith("//") || value.includes("\\")) return undefined;
  return value;
}
